// @flow

import * as React from 'react';
import {useAccount, useAnalytics, useLocation} from 'docs/data';
import cn from 'classnames';

import Box from 'sail/Box';
import Badge from 'sail/Badge';
import Group from 'sail/Group';
import {CaptionExtra} from 'sail/Text';
import {defineMessages, useIntl} from 'react-intl';

import StripeCluiTerminalContent from './content';
import StripeCluiTerminalInput from './input';
import StripeCluiTerminalPane from './pane';
import StripeCluiResize from './resize';
import StripeCluiButtonLink from '../components/ButtonLink';
import FeedbackAction from '../components/FeedbackAction/FeedbackAction';
import KeyboardShortcuts from '../components/KeyboardShortcuts';
import SplitPaneIcon from '../components/SplitPaneIcon';
import {tree} from '../generated_tree';
import {execute} from '../root';
import {giveSuggestion} from '../helpers/cli/suggestions';
import {white_space} from '../helpers/cli/general';
import {useStripeClui} from '../helpers/react/provider';
import {useResizable} from '../helpers/react/hooks';
import {
  KEY_MAPPINGS as KEYS,
  keyMappingChecker,
  shouldPreventDefault,
  shouldCapture,
  isCharacterEntry,
  shouldScrollToInput,
} from '../helpers/cli/key_mappings';
import {MAX_PANE_COUNT} from '../helpers/constants';
import {InputCommand} from './command';

import 'docs/components/core/CodeBlock/CodeBlock.scss';

const messages = defineMessages({
  popoutWindow: {
    id: 'docsApp.popoutWindow',
    description: 'Label to open CLUI terminal in a new window.',
    defaultMessage: 'Open as separate window',
  },
  minimize: {
    id: 'docsApp.minimize',
    description: 'Label to minimize CLUI terminal.',
    defaultMessage: 'Minimize',
  },
  expand: {
    id: 'docsApp.expand',
    description: 'Label to expand a minimized CLUI terminal.',
    defaultMessage: 'Expand',
  },
  close: {
    id: 'docsApp.close',
    description: 'Label to close CLUI terminal',
    defaultMessage: 'Close',
  },
  addPane: {
    id: 'docsApp.addPane',
    description:
      'Label for the action to add another pane to the CLUI terminal',
    defaultMessage: 'New pane',
  },
  testmode: {
    id: 'docsApp.clui.testmode',
    description:
      'Label for badge which indicates the terminal affects test data.',
    defaultMessage: 'Test mode',
  },
});

export const StripeCluiTerminal = (props: {}) => {
  const {
    state: {openState, size, panes, focusedPane, isFocused, locked},
    setFocus,
    open,
    close,
    toggleMinimize,
    addPane,
    focusPane,
    updatePane,
  } = useStripeClui();

  const {
    command,
    cursorIndex,
    history,
    arrowHistory,
    alteredHistory,
    alteredIndex,
    runCommand,
    suggestions,
    suggestionIndex,
    allowInput,
  } = panes[focusedPane];

  const {location} = useLocation();

  React.useEffect(() => {
    if (location?.query?.shell === 'true') {
      open();
    }
    // This is a mount-only effect...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analytics = useAnalytics();

  const {account} = useAccount();
  const apiKey = account.keys.secret;

  const {
    resizedLength,
    handleMouseDown: handleTerminalResize,
    isDragging,
  } = useResizable({direction: 'vertical'});

  const inputLineRef = React.useRef<HTMLDivElement | null>(null);
  const headerRef = React.useRef(null);

  const handleTerminalOpenKeyboardShortcut = React.useCallback(
    (event: KeyboardEvent) => {
      const ctrl = event.ctrlKey ? event.ctrlKey : event.key === 'Control';
      if (ctrl && event.key === '`') {
        if (openState === 'closed') {
          open();
        } else {
          toggleMinimize();
        }
      }
    },
    [open, openState, toggleMinimize],
  );

  React.useEffect(() => {
    document.addEventListener('keydown', handleTerminalOpenKeyboardShortcut);
    return () => {
      document.removeEventListener(
        'keydown',
        handleTerminalOpenKeyboardShortcut,
      );
    };
  }, [handleTerminalOpenKeyboardShortcut]);

  React.useEffect(() => {
    if (runCommand) {
      runCurrentCommand();
      updatePane({pane: {runCommand: false}});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runCommand]);

  const intl = useIntl();
  const closeLabel = intl.formatMessage(messages.close);
  const minimizeLabel = intl.formatMessage(messages.minimize);
  const expandLabel = intl.formatMessage(messages.expand);

  // Select a suggestion.
  const enterSuggestion = (idx): void => {
    const next_segment = suggestions[idx][0];
    const new_command = `${command.slice(0, command.lastIndexOf(' ') + 1) +
      next_segment} ${command.slice(cursorIndex)}`;
    const new_cursor = new_command.length;

    const new_altered = alteredHistory;
    new_altered[alteredIndex].command = new_command;

    updatePane({
      pane: {
        command: new_command,
        alteredHistory: new_altered,
        cursorIndex: new_cursor,
      },
    });
  };

  // Normally `UPDATE_PANE` will apply to the currently focused pane,
  // but in this case we want to explicitly set the `paneIndex` so if
  // the user executes a command and quickly clicks to another pane
  // before it resolves the allowInput pertains to the correct pane.
  const setAllowInput = (paneIndex) => (allowed) => {
    updatePane({
      paneIndex,
      pane: {allowInput: allowed},
    });
  };

  function runCurrentCommand() {
    setAllowInput(focusedPane)(false);
    const parsedCommand = new InputCommand(command);

    const output = execute(
      parsedCommand,
      apiKey,
      setAllowInput(focusedPane),
    ) ?? <> </>;

    const info = parsedCommand;
    if (info?.executable !== '') {
      analytics.trackAction('stripe_clui_execute_command', info);
    }
    const new_history = history;
    new_history.push({command, output});
    const new_arrow_history = arrowHistory;
    new_arrow_history.push({command, output});
    const new_altered_history = JSON.parse(JSON.stringify(new_arrow_history));
    new_altered_history.push({command: '', output: ''});

    updatePane({
      pane: {
        command: '',
        history: new_history,
        arrowHistory: new_arrow_history,
        alteredHistory: new_altered_history,
        alteredIndex: new_altered_history.length - 1,
        cursorIndex: 0,
        suggestions: giveSuggestion([''], tree),
        suggestionIndex: -1,
      },
    });
  }

  // Handle a key down event.
  const handleKeyDown = (event) => {
    if (!shouldCapture({isFocused, allowInput, event})) {
      return;
    }

    event.stopPropagation();

    if (shouldPreventDefault({event, isFocused, allowInput})) {
      event.preventDefault();
    }

    const isKeyMapping = keyMappingChecker(event);

    // Enter (Select suggestion / run command)
    if (isKeyMapping(KEYS.ENTER)) {
      if (suggestionIndex < 0) {
        const shouldScrollToPrompt = allowInput && !command.length;

        runCurrentCommand();

        if (shouldScrollToPrompt) {
          // If the user hits enter with an empty prompt we want to
          // make sure the prompt remains visible. The setTimeout is
          // necessary because we need the wait for the browser to
          // render the next prompt before referencing it.
          setTimeout(() => {
            if (inputLineRef.current) {
              inputLineRef.current.scrollIntoView({block: 'nearest'});
            }
          }, 0);
        }
      } else {
        enterSuggestion(suggestionIndex);
      }
    }

    // CTRL+V / CMD+V (Paste)
    else if (
      isKeyMapping(KEYS.CTRL_V, KEYS.CMD_V) &&
      navigator &&
      navigator.clipboard
    ) {
      navigator.clipboard
        .readText()
        .then((text) => {
          const segment = text.replace(white_space, ' ');
          const new_command = `${command.slice(0, cursorIndex) +
            segment} ${command.slice(cursorIndex)}`;
          const new_cursor = cursorIndex + segment.length + 1;

          const new_altered = alteredHistory;
          new_altered[alteredIndex].command = new_command;

          updatePane({
            pane: {
              command: new_command,
              alteredHistory: new_altered,
              cursorIndex: new_cursor,
            },
          });
        })
        .catch((err) => {});
    }

    // CTRL+U (delete whole line)
    else if (isKeyMapping(KEYS.CTRL_U)) {
      const new_cursor = 0;
      const new_command = '';

      updatePane({
        pane: {
          command: new_command,
          cursorIndex: new_cursor,
        },
      });
    }

    // CTRL+W / ALT+BACKSPACE (delete previous word)
    else if (isKeyMapping(KEYS.CTRL_W, KEYS.ALT_BACKSPACE)) {
      if (cursorIndex <= 0) {
        return;
      }

      const new_cursor =
        command
          .slice(0, cursorIndex + 1)
          .trimEnd()
          .lastIndexOf(' ') + 1;

      const new_command =
        new_cursor === -1
          ? command.slice(cursorIndex)
          : command.slice(0, new_cursor) + command.slice(cursorIndex);
      const new_altered = alteredHistory;
      new_altered[alteredIndex].command = new_command;

      updatePane({
        pane: {
          command: new_command,
          alteredHistory: new_altered,
          cursorIndex: new_cursor,
        },
      });
    }

    // CMD+K / CTRL+L (clear screen)
    else if (isKeyMapping(KEYS.CMD_K, KEYS.CTRL_L)) {
      updatePane({
        pane: {history: []},
      });
    }

    // Escape (out of suggestions)
    else if (isKeyMapping(KEYS.ESCAPE)) {
      updatePane({
        pane: {suggestionIndex: -1},
      });
    }

    // Shift tab (highlight previous suggestion)
    else if (isKeyMapping(KEYS.SHIFT_TAB)) {
      let newSuggestionIndex = suggestionIndex;
      if (suggestions.length > 0 && suggestionIndex < 0) {
        newSuggestionIndex = 0;
      } else if (suggestionIndex >= 0) {
        let index = suggestionIndex;
        if (suggestionIndex === 0) {
          index = suggestions.length;
        }
        newSuggestionIndex = (index - 1) % suggestions.length;
      }

      updatePane({
        pane: {
          suggestionIndex: newSuggestionIndex,
          displayOn: 'suggestion',
        },
      });
    }

    // Tab (highlight next suggestion)
    else if (isKeyMapping(KEYS.TAB)) {
      let newSuggestionIndex = suggestionIndex;
      if (suggestions.length === 1) {
        enterSuggestion(0);
      } else if (suggestions.length > 0 && suggestionIndex < 0) {
        newSuggestionIndex = 0;
      } else if (suggestionIndex >= 0) {
        newSuggestionIndex = (suggestionIndex + 1) % suggestions.length;
      }

      updatePane({
        pane: {
          suggestionIndex: newSuggestionIndex,
          displayOn: 'suggestion',
        },
      });
    }

    // CMD+ArrowLeft / CTRL+ArrowLeft / CTRL+A (jump to the beginning of the line)
    else if (
      isKeyMapping(KEYS.CMD_ARROW_LEFT, KEYS.CTRL_ARROW_LEFT, KEYS.CTRL_A)
    ) {
      const new_index = 0;

      updatePane({
        pane: {
          cursorIndex: new_index,
        },
      });
    }

    // CMD+ArrowRight / CTRL+ArrowRight / CTRL+E (jump to the end of the line)
    else if (
      isKeyMapping(KEYS.CMD_ARROW_RIGHT, KEYS.CTRL_ARROW_RIGHT, KEYS.CTRL_E)
    ) {
      const new_index = command.length + 1;

      updatePane({
        pane: {
          cursorIndex: new_index,
        },
      });
    }

    // CMD+[ / CTRL+[ (switch to pane to the left)
    else if (
      isKeyMapping(KEYS.CTRL_LEFT_BRACKET, KEYS.CMD_LEFT_BRACKET) &&
      panes.length > 1
    ) {
      const newFocusIndex =
        focusedPane === 0 ? panes.length - 1 : focusedPane - 1;

      focusPane(newFocusIndex);
    }

    // CMD+] / CTRL+] (switch to pane to the right)
    else if (
      isKeyMapping(KEYS.CTRL_RIGHT_BRACKET, KEYS.CMD_RIGHT_BRACKET) &&
      panes.length > 1
    ) {
      focusPane((focusedPane + 1) % panes.length);
    }

    // CMD+D / Alt+Shift+D (add new pane)
    else if (isKeyMapping(KEYS.CMD_D, KEYS.ALT_SHIFT_D)) {
      addPane();
    }

    // Arrow up (previous history)
    else if (isKeyMapping(KEYS.ARROW_UP)) {
      if (alteredIndex <= 0) {
        return;
      }
      const index = alteredIndex - 1;

      updatePane({
        pane: {
          command: alteredHistory[index].command,
          cursorIndex: alteredHistory[index].command.length,
          alteredIndex: index,
        },
      });
    }

    // Arrow down (next history)
    else if (isKeyMapping(KEYS.ARROW_DOWN)) {
      if (alteredIndex >= alteredHistory.length - 1) {
        return;
      }
      const index = alteredIndex + 1;

      updatePane({
        pane: {
          command: alteredHistory[index].command,
          cursorIndex: alteredHistory[index].command.length,
          alteredIndex: index,
        },
      });
    }

    // Arrow left (move cursor left)
    else if (isKeyMapping(KEYS.ARROW_LEFT)) {
      if (cursorIndex <= 0) {
        return;
      }
      const new_index = cursorIndex - 1;

      updatePane({
        pane: {
          cursorIndex: new_index,
        },
      });
    }

    // Arrow right (move cursor right)
    else if (isKeyMapping(KEYS.ARROW_RIGHT)) {
      if (cursorIndex >= command.length) {
        return;
      }
      const new_index = cursorIndex + 1;

      updatePane({
        pane: {
          cursorIndex: new_index,
        },
      });
    }

    // Backspace (delete previous character)
    else if (isKeyMapping(KEYS.BACKSPACE)) {
      if (cursorIndex <= 0) {
        return;
      }
      const new_command =
        command.slice(0, cursorIndex - 1) + command.slice(cursorIndex);
      const new_cursor = cursorIndex - 1;
      const new_altered = alteredHistory;
      new_altered[alteredIndex].command = new_command;

      updatePane({
        pane: {
          command: new_command,
          alteredHistory: new_altered,
          cursorIndex: new_cursor,
        },
      });
    }

    // Normal (append input to the command)
    else if (isCharacterEntry(event)) {
      const new_command =
        command.slice(0, cursorIndex) + event.key + command.slice(cursorIndex);
      const old_length = command.length;
      const new_length = new_command.length;
      let new_cursor = cursorIndex + 1;
      if (old_length === new_length) {
        new_cursor = cursorIndex;
      }
      const new_altered = alteredHistory;
      new_altered[alteredIndex].command = new_command;

      updatePane({
        pane: {
          command: new_command,
          alteredHistory: new_altered,
          cursorIndex: new_cursor,
        },
      });
    }

    const elmnt = inputLineRef.current;
    if (elmnt && shouldScrollToInput({isFocused, allowInput, event})) {
      updatePane({pane: {displayOn: 'input'}});
      elmnt.scrollIntoView({block: 'nearest'});
    }

    return false;
  };

  let terminalHeight;
  if (openState === 'closed') {
    terminalHeight = 0;
  } else if (size === 'minimized') {
    terminalHeight = headerRef.current?.clientHeight || 44;
  } else {
    terminalHeight = `calc(50% + ${resizedLength}px)`;
  }

  return (
    <div
      className={cn('StripeCluiTerminal', {
        'StripeCluiTerminal--focused': isFocused,
        'StripeCluiTerminal--transition': !isDragging,
        'StripeCluiTerminal--normal': size === 'normal' && openState === 'open',
      })}
      style={{
        height: terminalHeight,
      }}
      tabIndex={0}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    >
      <div ref={headerRef}>
        <Box
          className="StripeCluiTerminalHeader"
          background={'blue900'}
          padding={{vertical: 12, horizontal: 24}}
          flex={{
            direction: 'row',
            wrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
          style={{position: 'relative'}}
        >
          {size === 'normal' && (
            <StripeCluiResize onMouseDown={handleTerminalResize} />
          )}
          <Group
            direction="horizontal"
            spacing={8}
            alignItems="center"
            style={{marginRight: 'auto'}}
          >
            <CaptionExtra>Stripe Shell</CaptionExtra>
            <Badge
              color="yellow"
              label={intl.formatMessage(messages.testmode)}
            />
          </Group>
          <Group direction="horizontal" spacing={16}>
            {size !== 'minimized' && (
              <Box
                flex={{
                  direction: 'row',
                  wrap: 'nowrap',
                  alignItems: 'baseline',
                }}
                divider={{side: 'right', color: 'medium', width: 1}}
                padding={{right: 16}}
                className="StripeCluiActionGroup"
              >
                <KeyboardShortcuts />
                <FeedbackAction />
              </Box>
            )}
            {!locked &&
              panes.length < MAX_PANE_COUNT &&
              size !== 'minimized' && (
                <StripeCluiButtonLink
                  label={<SplitPaneIcon />}
                  tip={intl.formatMessage(messages.addPane)}
                  onClick={addPane}
                />
              )}
            <StripeCluiButtonLink
              tip={size === 'minimized' ? expandLabel : minimizeLabel}
              icon={size === 'minimized' ? 'chevronUp' : 'chevronDown'}
              onClick={toggleMinimize}
            />
            <StripeCluiButtonLink
              tip={closeLabel}
              icon="cancel"
              onClick={close}
            />
          </Group>
        </Box>
      </div>
      <StripeCluiTerminalContent>
        {panes.map((_, paneIdx) => (
          <StripeCluiTerminalPane
            handleKeyDown={handleKeyDown}
            handleSuggestion={enterSuggestion}
            suggestions={suggestions}
            inputLineHeight={inputLineRef.current?.clientHeight ?? 0}
            cliInput={
              <div ref={focusedPane === paneIdx ? inputLineRef : null}>
                <StripeCluiTerminalInput paneIdx={paneIdx} />
              </div>
            }
            paneIdx={paneIdx}
            key={paneIdx}
          />
        ))}
      </StripeCluiTerminalContent>
    </div>
  );
};
